/**
 * Round-3-QA-fixes plan, U3 — the "Add segmentation" drawer's "what's still
 * unused in the library" derivation, extracted to a pure function so it's
 * testable without a DOM (`bun:test` has no DOM under `mini-apps/`, same
 * reason as `rung-counts.ts` / `rung-selection.ts`).
 *
 * ── DIAGNOSIS (U3, 2026-09-11) ───────────────────────────────────────────────
 * Hypothesis 1 (server drops a segmentation identity whose head resolves to a
 * branch version) does NOT reproduce: `api.ts#getLibrary` maps over EVERY
 * non-archived `segmentationIdentities` row unconditionally — a head that
 * fails to resolve (`branch_play_id` filter) only makes `head` `undefined` for
 * that one entry, which downgrades `isMapped`/`headVersionNumber`, it never
 * removes the identity from the response array. So the library payload this
 * function receives already contains every non-archived segmentation.
 *
 * The actual bug is client-side (hypothesis 2): the exclusion set used to be
 * built as an inline `new Set([...rungs, ...(pendingRungs ?? [])].map((r) =>
 * r.segmentationId))` expression buried directly in JSX
 * (`QualifyStep.tsx:1096-1098`, the coding standards' own cited
 * anti-pattern) — untestable, and a second, independent filter re-read
 * `existingSegmentationIds` inside `AddSegmentationDrawerBody`. Consolidated
 * here into one definition both call sites compose with.
 *
 * ── CONTRACT ─────────────────────────────────────────────────────────────────
 * A library segmentation is "available" (still addable to this ladder) unless
 * its `id` matches the `segmentationId` of a CURRENT rung or a PENDING
 * (not-yet-persisted) rung on THIS playbook's draft. Scoped to this ladder
 * only: `usageCount`/`isMapped`, or use by a DIFFERENT playbook, never affect
 * the result (do-NOT-create list, U3).
 *
 * Inside a play (`playId` given — plan 2026-09-28-006 U3a, rulebook R11), the
 * candidates are {@link pickerRows}: Library rows and this play's own copies
 * and originals, each its own row, never an archived-in-Library piece or
 * another play's row. A Library row stays available while only its copy is a
 * group (picking it points a group at the Library piece — the ladder write
 * never substitutes ids), and a copy is available unless it is itself a
 * group. `playId` omitted (the playbook doorway, where every row already
 * carries `owningPlayId: null`) is byte-identical to before.
 */

import { pickerRows } from './play-ownership';
import type { PlaybooksLibraryResponse, PlaybooksSegmentationSummary } from './schemas';

export function availableSegmentations(
  library: PlaybooksLibraryResponse | undefined,
  rungs: readonly { segmentationId: string }[],
  pendingRungs: readonly { segmentationId: string }[] = [],
  playId?: string,
): PlaybooksSegmentationSummary[] {
  if (library === undefined) return [];
  const usedSegmentationIds = new Set(
    [...rungs, ...pendingRungs].map((rung) => rung.segmentationId),
  );
  // Outside a play every row already carries `owningPlayId: null`
  // (`pickerRows` is a no-op there), so this stays byte-identical to the
  // pre-U5 behavior.
  if (playId === undefined) {
    return library.segmentations.filter((segmentation) => !usedSegmentationIds.has(segmentation.id));
  }
  return pickerRows(library.segmentations, playId).filter(
    (segmentation) => !usedSegmentationIds.has(segmentation.id),
  );
}
